import { MRT_Row } from "material-react-table";
import { newSalespersonContact } from "../../../../../../models";
import type { Job, Contact } from "../../../../../../models";
import { isFieldRequired } from "../../../../../../utilities/jobValidation";
import { CREATE_NEW_SALESPERSON } from "../../../../../../utilities/constants";
import { ColumnContext } from "../standard-column-types";
import { SalespersonColumnViewModel } from "./Salesperson.column.types";

/**
 * Hook that provides the SalespersonColumn ViewModel
 * @param context - The column context containing state and helpers
 * @returns ViewModel with all business logic functions
 */
export function useSalespersonColumnViewModel(context: ColumnContext): SalespersonColumnViewModel {
	/**
	 * Get combined list of props salespersons + new salespersons
	 */
	function getAllSalespersons(): Contact[] {
		return [...(context.props.salespersons || []), ...context.newSalespersons];
	}

	/**
	 * Get current selected salesperson for job
	 * Uses row.original directly instead of O(n) jobs.find() lookup
	 */
	function getSelectedSalesperson(job: Job): Contact | null {
		const hasNewSalesperson = job?.salespersonIds?.includes(CREATE_NEW_SALESPERSON);
		const allSalespersons = getAllSalespersons();

		if (hasNewSalesperson) {
			return { id: CREATE_NEW_SALESPERSON, name: job?.newSalesperson?.name || "New Salesperson" } as Contact;
		}

		return allSalespersons.find((contact) => job?.salespersonIds?.includes(contact.id)) || null;
	}

	/**
	 * Compute required state from context
	 */
	function getIsRequired(row: MRT_Row<Job>): boolean {
		const rowContext = context.getRowValidationContext(row);
		return isFieldRequired("salesperson", rowContext);
	}

	/**
	 * Handle Cell value change (string input, new salesperson, existing, clear)
	 */
	function handleChange(jobId: string, value: string | Contact | null, reason: string, row: MRT_Row<Job>): void {
		// Ignore onChange events that are not from actual user selection
		if (reason === "blur") {
			return;
		}

		const newJobs = [...context.jobs];
		const newJob = newJobs.find((j) => j.id === jobId);
		if (!newJob) return;
		const index = newJobs.indexOf(newJob);
		const allSalespersons = getAllSalespersons();

		if (typeof value === "string") {
			// Handle free text input
			const newValue = value.startsWith("New Salesperson:") ? value.replace("New Salesperson: ", "") : value;

			if (newValue !== "") {
				// Check if salesperson already exists
				const existingSalesperson = allSalespersons.find((sp) => sp.name.toLowerCase() === newValue.toLowerCase());

				if (existingSalesperson) {
					// Use existing salesperson
					newJob.salespersonIds = [existingSalesperson.id];
					newJob.newSalesperson = undefined;
				} else {
					// Create new salesperson
					newJob.salespersonIds = [CREATE_NEW_SALESPERSON];
					newJob.newSalesperson = { name: newValue, phone: "" };

					// Add to local salespersons list with a temporary ID
					const tempId = `temp-${Date.now()}-${Math.random()}`;
					const firstName = newValue.split(" ").at(0) || "";
					const lastName = newValue.split(" ").slice(1).join(" ") || "";

					const newSalesperson = newSalespersonContact(tempId, newValue, firstName, lastName);

					// Only add if not already in local list
					if (!context.newSalespersons.some((sp) => sp.name.toLowerCase() === newValue.toLowerCase())) {
						context.setNewSalespersons([...context.newSalespersons, newSalesperson]);
					}
				}
			}
		} else if (value && value.id === CREATE_NEW_SALESPERSON) {
			// Selected "New Salesperson" option
			const name = value.name.replace("New Salesperson: ", "");

			// Check if salesperson already exists
			const existingSalesperson = allSalespersons.find((sp) => sp.name.toLowerCase() === name.toLowerCase());

			if (existingSalesperson) {
				// Use existing salesperson
				newJob.salespersonIds = [existingSalesperson.id];
				newJob.newSalesperson = undefined;
			} else {
				newJob.salespersonIds = [CREATE_NEW_SALESPERSON];
				newJob.newSalesperson = { name, phone: "" };

				// Add to local salespersons list with a temporary ID
				const tempId = `temp-${Date.now()}-${Math.random()}`;
				const firstName = name.split(" ").at(0) || "";
				const lastName = name.split(" ").slice(1).join(" ") || "";

				const newSalesperson = newSalespersonContact(tempId, name, firstName, lastName);

				// Only add if not already in local list
				if (!context.newSalespersons.some((sp) => sp.name.toLowerCase() === name.toLowerCase())) {
					context.setNewSalespersons([...context.newSalespersons, newSalesperson]);
				}
			}
		} else if (value && value.id !== CREATE_NEW_SALESPERSON) {
			// Check if this is a locally-created salesperson
			if (context.isNewSalesperson(value.id)) {
				// This is a locally-created salesperson, treat as new
				const localSp = context.findNewSalesperson(value.id);
				if (localSp) {
					newJob.salespersonIds = [CREATE_NEW_SALESPERSON];
					newJob.newSalesperson = { name: localSp.name, phone: localSp.mobilePhone };
				}
			} else {
				// This is a real existing salesperson from props
				newJob.salespersonIds = [value.id];
				newJob.newSalesperson = undefined;
			}
		} else if (value === null && reason === "clear") {
			// User explicitly cleared the selection
			newJob.salespersonIds = [];
			newJob.newSalesperson = undefined;
		} else {
			// Ignore other cases (like null from blur, etc.)
			return;
		}

		row._valuesCache.salespersonIds = newJob.salespersonIds;
		newJobs[index] = newJob;
		context.setJobs(newJobs);
		context.validateJobs([newJob], { updateState: true, clearPrevious: false });
	}

	/**
	 * Handle AggregatedCell group update - updates all jobs in the group
	 */
	function handleGroupChange(row: MRT_Row<Job>, value: string | Contact | null, reason: string): void {
		// Ignore onChange events that are not from actual user selection
		if (reason === "blur") {
			return;
		}

		const allSalespersons = getAllSalespersons();

		// Determine salespersonIds and newSalesperson based on value
		let salespersonIds: string[] = [];
		let newSalesperson: { name: string; phone: string } | undefined = undefined;

		if (typeof value === "string") {
			const newValue = value.startsWith("New Salesperson:") ? value.replace("New Salesperson: ", "") : value;
			if (newValue !== "") {
				const existingSalesperson = allSalespersons.find((sp) => sp.name.toLowerCase() === newValue.toLowerCase());
				if (existingSalesperson) {
					salespersonIds = [existingSalesperson.id];
				} else {
					salespersonIds = [CREATE_NEW_SALESPERSON];
					newSalesperson = { name: newValue, phone: "" };

					// Add to local salespersons list
					const tempId = `temp-${Date.now()}-${Math.random()}`;
					const firstName = newValue.split(" ").at(0) || "";
					const lastName = newValue.split(" ").slice(1).join(" ") || "";
					const newSpContact = newSalespersonContact(tempId, newValue, firstName, lastName);
					if (!context.newSalespersons.some((sp) => sp.name.toLowerCase() === newValue.toLowerCase())) {
						context.setNewSalespersons([...context.newSalespersons, newSpContact]);
					}
				}
			}
		} else if (value && value.id === CREATE_NEW_SALESPERSON) {
			const name = value.name.replace("New Salesperson: ", "");
			const existingSalesperson = allSalespersons.find((sp) => sp.name.toLowerCase() === name.toLowerCase());
			if (existingSalesperson) {
				salespersonIds = [existingSalesperson.id];
			} else {
				salespersonIds = [CREATE_NEW_SALESPERSON];
				newSalesperson = { name, phone: "" };

				const tempId = `temp-${Date.now()}-${Math.random()}`;
				const firstName = name.split(" ").at(0) || "";
				const lastName = name.split(" ").slice(1).join(" ") || "";
				const newSp = newSalespersonContact(tempId, name, firstName, lastName);
				if (!context.newSalespersons.some((sp) => sp.name.toLowerCase() === name.toLowerCase())) {
					context.setNewSalespersons([...context.newSalespersons, newSp]);
				}
			}
		} else if (value && value.id !== CREATE_NEW_SALESPERSON) {
			if (context.isNewSalesperson(value.id)) {
				const localSp = context.findNewSalesperson(value.id);
				if (localSp) {
					salespersonIds = [CREATE_NEW_SALESPERSON];
					newSalesperson = { name: localSp.name, phone: localSp.mobilePhone };
				}
			} else {
				salespersonIds = [value.id];
			}
		} else if (value === null && reason === "clear") {
			salespersonIds = [];
		} else {
			return;
		}

		// Update all jobs in the group
		const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
			job.salespersonIds = salespersonIds;
			job.newSalesperson = newSalesperson;
		});

		context.setJobs(newJobs);
		context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
	}

	return {
		getAllSalespersons,
		getSelectedSalesperson,
		getIsRequired,
		handleChange,
		handleGroupChange,
	};
}
